import { useId, useState } from 'react';
import { isYearMonth, lastMonths } from '@blimo/shared';
import { Input } from '../components/input.jsx';
import { Label } from '../components/label.jsx';
import { ToggleGroup } from '../components/toggle-group.jsx';
import { cn } from '../lib/utils.js';

const PRESETS = [6, 12, 24];

/**
 * Month range ("YYYY-MM" … "YYYY-MM") with presets „6/12/24 Monate“ (relative to today).
 * Calls `onChange` only with a valid range (from ≤ to).
 * @param {{
 *   from: string, to: string,
 *   onChange: (range: { from: string, to: string }) => void,
 *   presets?: number[],
 *   size?: 'default'|'sm',
 *   className?: string,
 * }} props
 */
export function MonthRangePicker({ from, to, onChange, presets = PRESETS, size = 'default', className }) {
  const id = useId();
  const [draft, setDraft] = useState({ from, to });
  const [synced, setSynced] = useState({ from, to });
  if (synced.from !== from || synced.to !== to) {
    // props changed from outside (URL, preset) → reset the draft
    setSynced({ from, to });
    setDraft({ from, to });
  }
  const invalid = isYearMonth(draft.from) && isYearMonth(draft.to) && draft.from > draft.to;

  /** @param {'from'|'to'} key @param {string} value */
  const update = (key, value) => {
    const next = { ...draft, [key]: value };
    setDraft(next);
    if (isYearMonth(next.from) && isYearMonth(next.to) && next.from <= next.to) onChange(next);
  };

  const presetValue = presets.find((n) => {
    const r = lastMonths(n);
    return r.from === from && r.to === to;
  });

  return (
    <div className={cn('flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-end', className)}>
      <div className="grid grid-cols-2 gap-2 sm:flex">
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${id}-from`} className="text-xs text-muted-foreground">
            Von (Monat)
          </Label>
          <Input
            id={`${id}-from`}
            type="month"
            value={draft.from}
            max={draft.to || undefined}
            onChange={(e) => update('from', e.target.value)}
            aria-invalid={invalid || undefined}
            className={cn('sm:w-40', size === 'sm' && 'h-(--control-height-sm)')}
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${id}-to`} className="text-xs text-muted-foreground">
            Bis (Monat)
          </Label>
          <Input
            id={`${id}-to`}
            type="month"
            value={draft.to}
            min={draft.from || undefined}
            onChange={(e) => update('to', e.target.value)}
            aria-invalid={invalid || undefined}
            className={cn('sm:w-40', size === 'sm' && 'h-(--control-height-sm)')}
          />
        </div>
      </div>
      <ToggleGroup
        label="Zeitraum-Vorauswahl"
        size={size}
        value={presetValue ? String(presetValue) : ''}
        onValueChange={(v) => onChange(lastMonths(Number(v)))}
        options={presets.map((n) => ({ value: String(n), label: `${n} Monate` }))}
      />
      {invalid && (
        <p role="alert" className="text-xs font-medium text-destructive sm:basis-full">
          Der Beginn muss vor dem Ende liegen.
        </p>
      )}
    </div>
  );
}
